import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DialogContent, Grid, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { StatusCell } from "components/atoms";
import { renderProgress } from "components/atoms/PercentProgressCell";
import {
  AppTable,
  ImportDocumentDialog,
  ImportErrorDialog,
} from "components/molecules";
import {
  ColorHelper,
  DownloadHelper,
  StatusHelper,
  TableHelper,
} from "helpers";
import { usePaginationRequest } from "hooks";
import { isArray, isEmpty, isUndefined } from "lodash";
import {
  EImportType,
  ELoadingOrderStatus,
  IDownloadExcelRequest,
  IImportLoadingOrderDocument,
  ILoadingOrder,
  IPaginateApiResponse,
  IPaginationRequest,
} from "model";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { DownloadActions, LoadingOrderActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import LoadingOrderApiService from "services/LoadingOrderApiService";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

const LoadingOrderDocument = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const data = useAppSelector(
    (state) => state.LoadingOrderReducer.loadingOrder!
  );
  const action = useAppSelector((state) => state.LoadingOrderReducer.action);
  const errors = useAppSelector((state) => state.ErrorReducer.errors);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );
  const [downloading, setDownloading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    searchText: searchText,
  });
  const [file, setFile] = useState<FileList | null>(null);
  const [currentItem, setCurrentItem] = useState<ILoadingOrder | undefined>(
    undefined
  );

  const [open, setOpen] = useState<boolean>(false);
  const [loTemplateDownloading, setLoTemplateDownloading] = useState(false);

  const [importFailedDialogVisible, setImportFailedDialogVisible] =
    useState<boolean>(false);

  const [importType, setImportType] = useState<EImportType>(
    EImportType.IMPORT_NEW
  );
  const isImportNewDocs = importType === EImportType.IMPORT_NEW;

  const onCellClick = (event: any) => {
    if (event.colDef.field !== "action") {
      history.push(
        `/receive/loading-order/${event.row.id}&${event.row.doImportCode}`
      );
    }
  };

  const handleSelectFile = (files: FileList | null) => {
    if (!isEmpty(files)) {
      setImportType(importType);
      setFile(files);
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setOpen(false);
    setCurrentItem(undefined);
  };
  const handleConfirmImport = () => {
    _importLoadingOrderDocument();
  };

  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    ILoadingOrder[]
  >(
    request,
    LoadingOrderApiService.getAllLoadingOrder,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );
  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _getAllLoadingOrder = (pageNumber = 1) => {
    dispatch(
      LoadingOrderActions.getAllLoadingOrder.request({
        pageNumber: pageNumber,
        pageSize: 100,
        searchText: searchText,
      })
    );
  };
  const _importLoadingOrderDocument = () => {
    const request: IImportLoadingOrderDocument = {
      file: file?.item(0) as File,
      LOImportCode: currentItem?.doImportCode || "",
      loadingOrderHeaderId: currentItem?.id || "",
      loadingOrderHeaderDescription: currentItem?.description || "",
    };
    dispatch(LoadingOrderActions.importLoadingOrderDocument.request(request));
  };

  const importDocsSuccess = action.includes(
    LoadingOrderActions.importLoadingOrderDocument.successName
  );
  const importDocsFailed = action.includes(
    LoadingOrderActions.importLoadingOrderDocument.failedName
  );
  const _downloadLO = () => {
    const params: IDownloadExcelRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<ILoadingOrder[]>).totalRecords,
      pageNumber: FIRST_PAGE,
      fileName: EXPORT_FILE_NAME.loadingOrder,
    };
    dispatch(DownloadActions.downloadLODocument.request(params));
  };

  useEffect(() => {
    _getAllLoadingOrder();
  }, [searchText]);

  useEffect(() => {
    if (importDocsSuccess) {
      setRefresh(true);
      handleCancel();
      _getAllLoadingOrder();
    }
    if (importDocsFailed) {
      if (isArray(errors) || !isUndefined(errors)) {
        setOpen(false);
        // setImportFailedDialogVisible(true);
      }
    }
  }, [action]);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "doImportCode",
      headerName: I18nTranslator(translations.receive.loCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "deliveryDate",
      headerName: I18nTranslator(translations.receive.deliveryDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "status",
      headerName: I18nTranslator(translations.status),
      align: "left",
      size: "small",
      renderCell: ({ value }) => (
        <StatusCell
          bgcolor={ColorHelper.getLoadingOrderStatusColor(
            value as ELoadingOrderStatus
          )}
        >
          {StatusHelper.getLOStatusString(value)}
        </StatusCell>
      ),
    }),
    TableHelper.createCell({
      field: "doQuantity",
      headerName: I18nTranslator(translations.receive.quantityLO),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "sumSuggestCartonPick",
      headerName: I18nTranslator(translations.receive.sumSuggestCartonPick),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "sumSuggestOddPick",
      headerName: I18nTranslator(translations.receive.sumSuggestOddPick),
      size: 220,
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "preparedQuantity",
      headerName: I18nTranslator(translations.receive.prepared),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "deliveredQty",
      headerName: I18nTranslator(translations.receive.delivered),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "prepareProgress",
      headerName: I18nTranslator(translations.receive.preparedProgressPercent),
      size: "small",
      align: "left",
      sortable: true,
      type: "number",
      renderCell: renderProgress,
    }),
    TableHelper.createCell({
      field: "deliveryProgress",
      headerName: I18nTranslator(translations.receive.deliveryProgressPercent),
      size: "small",
      align: "left",
      sortable: true,
      type: "number",
      renderCell: renderProgress,
    }),

    TableHelper.createCell({
      field: "description",
      headerName: I18nTranslator(translations.receive.note),
      size: "full",
      align: "left",
    }),
  ];

  const onDownloadReceiveTemplate = async () => {
    const params: IDownloadExcelRequest = {
      fileName: EXPORT_FILE_NAME.loadingOrder,
    };
    dispatch(DownloadActions.downloadLODocumentForm.request(params));
  };

  return (
    <Fragment>
      <AppTable<ILoadingOrder>
        title={I18nTranslator(translations.routes.loadingOrderDocument)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onCellClick={onCellClick}
        onSearch={handleSearch}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchLoadingOrder
        )}
        onChangePage={handleChangePage}
        actionColumnProps={{
          hideDelete: true,
          hideEdit: true,
        }}
        hideAddNew
        onFileSelected={(files) => handleSelectFile(files)}
        onRowPressImport={(row) => {
          setImportType(EImportType.UPDATE);
          setCurrentItem(row);
        }}
        onPageSizeChange={_handlePageSizeChange}
        onDownloadClick={_downloadLO}
        isDownloading={downloading}
        headerButtonsLeft={
          <Stack direction={"row"} spacing={2} sx={{ marginRight: "10px" }}>
            <LoadingButton
              loading={loTemplateDownloading}
              startIcon={<Download />}
              onClick={onDownloadReceiveTemplate}
              variant="contained"
            >
              {I18nTranslator(translations.button.exportTemplate)}
            </LoadingButton>
          </Stack>
        }
      />
      <ImportDocumentDialog
        title={I18nTranslator(
          isImportNewDocs
            ? translations.receive.importDocs
            : translations.receive.updateDocs
        )}
        open={open}
        handleConfirm={handleConfirmImport}
        handleClose={handleCancel}
        loading={action.includes(
          LoadingOrderActions.importLoadingOrderDocument.requestName
        )}
        fileName={!isEmpty(file) && file ? file[0].name : ""}
        isImportNewDocs={isImportNewDocs}
        updateItemName={currentItem?.doImportCode || ""}
      />
      <ImportErrorDialog
        title={I18nTranslator(translations.receive.importError)}
        open={importFailedDialogVisible}
        handleReImport={() => {
          setImportFailedDialogVisible(false);
          handleCancel();
        }}
      >
        <DialogContent sx={{ maxHeight: "50vh" }}>
          {!isUndefined(errors) &&
            isArray(errors) &&
            (errors as string[]).map((error) => {
              return <Typography>{error}</Typography>;
            })}
        </DialogContent>
      </ImportErrorDialog>
    </Fragment>
  );
};

export default LoadingOrderDocument;
